import type {
  CmmdRuntimeDiagnosticsSnapshot,
  EnvironmentId,
  RuntimeDiagnosticsResponse,
} from "@cmmd-forge/contracts";

import type {
  CloudEnvironmentCatalogRow,
  CloudProjectCatalogRow,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import type { CloudEnvironmentLifecycleProjection } from "../../environments/primary/cloudEnvironmentLifecycleProjection";
import { projectRowsForEnvironment } from "../../cmmd/cloudEnvironmentGrouping";
import { isDestroyedStatus } from "../../cmmd/forgeRuntimeStatus";
import {
  forgeCloudEnvironmentLabelForRow,
  type ForgeCloudEnvironmentNamingContext,
} from "../../environments/forgeCloudEnvironmentLabel";
import {
  filterRuntimeInventoryServices,
  formatCurrentProcessEvidence,
  serviceEvidence,
  type LocalStatusCheck,
} from "./DiagnosticsSettingsPanel.logic";
import { cloudProviderStatus } from "./DiagnosticsCloudProviderStatus";
import type { CodexRuntimeReadiness } from "./codexRuntimeReadiness";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";

export interface CloudRuntimeView {
  readonly environmentId: EnvironmentId;
  readonly runtimeId: string;
  readonly cmmdOrganizationId: string | null;
  /**
   * The first project registered on this Environment, or null while it has
   * none. CMMD's lifecycle preflight receipt is issued per project, so the
   * Cloud panel still needs one project to ask about; the Environment itself
   * is what the picker lists.
   */
  readonly cmmdProjectId: number | null;
  readonly label: string;
  readonly httpBaseUrl: string | null;
  readonly wsBaseUrl: string | null;
  readonly createdAt: string | null;
  readonly lastConnectedAt: string | null;
  /** The only Cloud VM action source: CMMD forge-context catalog. */
  readonly source: "catalog";
  /** CMMD control-plane lifecycle state. This is not a local socket state. */
  readonly catalogStatus: CloudProjectCatalogRow["status"];
  readonly lifecycle?: CloudEnvironmentLifecycleProjection | null;
}

export interface CloudDiagnosticsSnapshot {
  readonly source: "selected_forge_runtime";
  readonly targetRuntimeId: string;
  readonly observedAt: string;
  readonly collectionOutcome: "matched" | "identity_mismatch" | "unavailable";
}

export interface CloudControlPlaneSnapshot {
  readonly source: "cmmd_forge_context";
  readonly targetRuntimeId: string;
  readonly observedAt: string;
  readonly collectionOutcome: "matched" | "identity_mismatch" | "unavailable";
}

export function makeCloudDiagnosticsSnapshot(input: {
  readonly runtime: CloudRuntimeView;
  readonly diagnostics: RuntimeDiagnosticsResponse | undefined;
}): CloudDiagnosticsSnapshot {
  const diagnostics = input.diagnostics;
  if (!diagnostics) {
    return {
      source: "selected_forge_runtime",
      targetRuntimeId: input.runtime.runtimeId,
      observedAt: "",
      collectionOutcome: "unavailable",
    };
  }
  const runtimeMatches = diagnostics.runtime.cmmdRuntimeId === input.runtime.runtimeId;
  const organizationMatches =
    input.runtime.cmmdOrganizationId === null ||
    diagnostics.runtime.cmmdRuntimeOrgId === input.runtime.cmmdOrganizationId;
  return {
    source: "selected_forge_runtime",
    targetRuntimeId: input.runtime.runtimeId,
    observedAt: diagnostics.fetchedAt,
    collectionOutcome: runtimeMatches && organizationMatches ? "matched" : "identity_mismatch",
  };
}

/** The CMMD snapshot must attest to the exact selected Cloud VM before display. */
export function makeCloudControlPlaneSnapshot(input: {
  readonly runtime: CloudRuntimeView;
  readonly snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined;
}): CloudControlPlaneSnapshot {
  const snapshot = input.snapshot;
  if (!snapshot) {
    return {
      source: "cmmd_forge_context",
      targetRuntimeId: input.runtime.runtimeId,
      observedAt: "",
      collectionOutcome: "unavailable",
    };
  }
  return {
    source: "cmmd_forge_context",
    targetRuntimeId: input.runtime.runtimeId,
    observedAt: snapshot.availability.observedAt ?? "",
    collectionOutcome:
      snapshot.target.runtimeId === input.runtime.runtimeId && snapshot.target.kind === "cloud-vm"
        ? "matched"
        : "identity_mismatch",
  };
}

export function formatRuntimeId(environmentId: string): string {
  return classifyEnvironmentId(environmentId).legacyRuntimeId ?? environmentId;
}

/**
 * One view per Environment (Cloud VM), never per project.
 *
 * CMMD registers many projects on one VM, so the project catalog rows are a
 * many-to-one over `environments`. Listing the rows turned one suspended VM
 * with three projects into three suspended targets named after the projects,
 * while Connections showed one Environment with three projects under it.
 */
export function makeCloudRuntimeViews(input: {
  /** The CMMD Environment catalog is the only actionable Cloud VM inventory. */
  readonly environments: ReadonlyArray<CloudEnvironmentCatalogRow>;
  /** Project rows, matched to their Environment for the per-project receipt. */
  readonly catalogRows: ReadonlyArray<CloudProjectCatalogRow>;
  /** Names each target by its Organization, as Connections does. */
  readonly namingContext: ForgeCloudEnvironmentNamingContext;
}): ReadonlyArray<CloudRuntimeView> {
  return input.environments.flatMap((environment) => {
    if (isDestroyedStatus(environment.status)) return [];
    const projects = projectRowsForEnvironment(environment, input.catalogRows);
    const firstProject = projects[0] ?? null;
    return [
      {
        environmentId: environment.environmentId as EnvironmentId,
        runtimeId: environment.runtimeId,
        cmmdOrganizationId: String(environment.cmmdOrganizationId),
        cmmdProjectId: firstProject?.cmmdProjectId ?? null,
        label: forgeCloudEnvironmentLabelForRow(environment, input.namingContext),
        httpBaseUrl: null,
        wsBaseUrl: null,
        createdAt: firstProject?.registeredAt ?? null,
        lastConnectedAt: null,
        source: "catalog" as const,
        catalogStatus: environment.status,
        lifecycle: environment.lifecycle ?? null,
      },
    ];
  });
}

export interface CloudVmStatusInput {
  readonly codexReadiness: CodexRuntimeReadiness;
  readonly diagnostics: RuntimeDiagnosticsResponse | undefined;
  readonly diagnosticsError: string | undefined;
  readonly environmentId: string;
  readonly connectionState: "connecting" | "connected" | "disconnected" | "error";
  readonly sessionAuthState: "authenticated" | "requires-auth" | "unknown";
  readonly selectedIsActive: boolean;
  readonly activeThreadCount: number;
  readonly httpBaseUrl: string | null;
  readonly wsBaseUrl: string | null;
  readonly lastError: string | null;
}

function forgeServerCheck(input: CloudVmStatusInput, hasDiagnostics: boolean): LocalStatusCheck {
  if (hasDiagnostics && input.diagnostics?.currentProcess) {
    return {
      check: "Forge server",
      state: "Ready",
      tone: "ready",
      evidence: formatCurrentProcessEvidence(input.diagnostics.currentProcess),
      jump: "processes",
    };
  }
  const tone =
    input.connectionState === "connected"
      ? "warning"
      : input.connectionState === "error"
        ? "error"
        : "muted";
  return {
    check: "Forge server",
    state: input.connectionState,
    tone,
    evidence: `Cloud VM ${input.environmentId}`,
    jump: "processes",
  };
}

function endpointCheck(
  check: "Web client" | "WebSocket",
  evidence: string,
  unavailableEvidence: string,
  hasDiagnostics: boolean,
): LocalStatusCheck {
  const available = evidence !== unavailableEvidence;
  if (available && !hasDiagnostics) {
    return {
      check,
      state: "Saved route",
      tone: "muted",
      evidence: `Route recorded locally; not verified: ${evidence}`,
      jump: "network",
    };
  }
  return {
    check,
    state: available ? "Ready" : "Unknown",
    tone: available ? "ready" : "muted",
    evidence,
    jump: "network",
  };
}

function runtimeStateCheck(input: CloudVmStatusInput): LocalStatusCheck {
  const suffix = input.activeThreadCount === 1 ? "" : "s";
  return {
    check: "Runtime state",
    state: input.selectedIsActive ? "Active" : "Saved",
    tone: input.selectedIsActive ? "ready" : "muted",
    evidence: `${input.activeThreadCount} active thread${suffix}`,
    jump: "processes",
  };
}

function logsCheck(selectedIsActive: boolean): LocalStatusCheck {
  return {
    check: "Logs directory",
    state: selectedIsActive ? "Runtime scoped" : "Select VM",
    tone: selectedIsActive ? "ready" : "muted",
    evidence: selectedIsActive
      ? "Logs are scoped to the active Cloud VM runtime"
      : "Switch to this VM to inspect live logs",
    jump: "logs",
  };
}

function errorsCheck(input: CloudVmStatusInput, erroredToolCount: number): LocalStatusCheck {
  const failedToolEvidence =
    erroredToolCount > 0
      ? `${erroredToolCount} failed tool check${erroredToolCount === 1 ? "" : "s"}`
      : null;
  const evidence = input.diagnosticsError ?? input.lastError ?? failedToolEvidence;
  return {
    check: "Recent cloud errors",
    state: evidence ? "Attention" : "Clear",
    tone: evidence ? "error" : "ready",
    evidence: evidence ?? "No cloud runtime error reported",
    jump: "errors",
  };
}

export function buildCloudVmStatusChecks(
  input: CloudVmStatusInput,
): ReadonlyArray<LocalStatusCheck> {
  const services = filterRuntimeInventoryServices(
    input.diagnostics?.localServices ?? [],
    input.diagnostics,
  );
  const webClientEvidence =
    input.httpBaseUrl ?? serviceEvidence(services, "Vite web", "Web client endpoint not reported");
  const webSocketEvidence =
    input.wsBaseUrl ?? serviceEvidence(services, "WebSocket", "WebSocket endpoint not reported");
  const erroredToolCount =
    input.diagnostics?.tools.filter((tool) => tool.status === "error").length ?? 0;
  const hasDiagnostics = input.selectedIsActive && Boolean(input.diagnostics);
  const providerStatus = cloudProviderStatus({
    codexReadiness: input.codexReadiness,
    sessionAuthState: input.sessionAuthState,
  });

  return [
    forgeServerCheck(input, hasDiagnostics),
    endpointCheck(
      "Web client",
      webClientEvidence,
      "Web client endpoint not reported",
      hasDiagnostics,
    ),
    endpointCheck(
      "WebSocket",
      webSocketEvidence,
      "WebSocket endpoint not reported",
      hasDiagnostics,
    ),
    { check: "Cloud providers", ...providerStatus, jump: "services" },
    runtimeStateCheck(input),
    logsCheck(input.selectedIsActive),
    errorsCheck(input, erroredToolCount),
  ];
}
